import { useEffect, useRef } from 'react';
import io from 'socket.io-client';
import handleParcelStatusUpdate from '@/utils/handleParcelStatusUpdate';
import { SOCKET_EVENTS, presentSocketNotification, addSocketListener, emitSocketEvent } from '.';
import { useNotification } from '@/context/NotificationContext';
import useApiCall from '@/services/api/useApiCall';
import { useSelector } from 'react-redux';

// Central socket connection hook
export default function useSocketConnection({ deviceToken, userId, location }) {
  const socketRef = useRef(null);
  const registeredRef = useRef(false);
  const { showNotification } = useNotification();
  const { makeApiCall } = useApiCall();
  // Try to derive user id from Redux store if not passed explicitly
  const authUser = useSelector((s) => s.auth?.user);
  const inferredUserId = userId || authUser?.userId || authUser?.id || authUser?.user_id || null;

  // Effect 1: Initialize socket connection (only when userId changes or on mount)
  useEffect(() => {
    // Skip if socket already exists and is connected
    if (socketRef.current?.connected) {
      return;
    }

    // Mobile-optimized socket configuration
    const isMobile =
      typeof window !== 'undefined' &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    socketRef.current = io(process.env.NEXT_PUBLIC_API_BASE_URL, {
      transports: ['websocket', 'polling'], // Fallback to polling on mobile
      reconnectionAttempts: isMobile ? 10 : 5, // More attempts on mobile
      reconnectionDelay: isMobile ? 2000 : 1000, // Longer delay on mobile
      timeout: isMobile ? 20000 : 10000, // Longer timeout on mobile
      forceNew: false, // Reuse connection
      autoConnect: true,
    });

    const cleanupFns = [];
    cleanupFns.push(
      addSocketListener(socketRef, SOCKET_EVENTS.CONNECT, () => {
        registeredRef.current = false; // Reset registration flag on new connection
      })
    );

    cleanupFns.push(
      addSocketListener(socketRef, SOCKET_EVENTS.LEG_NOTIFICATION, (data) => {
        console.log('[Socket] leg_notification received:', data);
        if (data.data?.parcelId && makeApiCall) {
          window.dispatchEvent(
            new CustomEvent(SOCKET_EVENTS.PARCEL_UPDATED, {
              detail: {
                parcelId: data.data.parcelId,
                notificationType: data.type,
              },
            })
          );
        }
        // Present templated notification (centralized):
        presentSocketNotification({
          eventName: SOCKET_EVENTS.LEG_NOTIFICATION,
          payload: data,
          showNotification,
        });
      })
    );

    cleanupFns.push(
      addSocketListener(socketRef, SOCKET_EVENTS.PARCEL_STATUS_UPDATE, (data) => {
        // Handle UI updates and in-app notifications via the existing handler
        // (handleParcelStatusUpdate triggers appropriate showNotification calls).
        handleParcelStatusUpdate(data, showNotification, makeApiCall);
        // Avoid duplicate toasts: handleParcelStatusUpdate already shows toasts for status updates.
      })
    );

    cleanupFns.push(
      addSocketListener(socketRef, SOCKET_EVENTS.DISCONNECT, () => {
        console.log('[Socket] Disconnected:', socketRef.current?.id);
        registeredRef.current = false; // Reset registration flag on disconnect
      })
    );

    return () => {
      if (socketRef.current) {
        cleanupFns.forEach((fn) => {
          try {
            if (typeof fn === 'function') fn();
          } catch (e) {
            console.warn('[Socket] Unsubscribe error', e);
          }
        });
        console.log('[Socket] Cleanup - Disconnecting:', socketRef.current?.id || 'unknown');
        if (socketRef.current.connected) {
          socketRef.current.disconnect();
        }
        socketRef.current = null;
        registeredRef.current = false;
      }
    };
  }, [inferredUserId, makeApiCall, showNotification]); // Only reconnect when userId changes

  // Effect 2: Register device when socket is connected and we have userId/deviceToken
  useEffect(() => {
    if (!socketRef.current?.connected) return;
    if (registeredRef.current) return; // Already registered
    if (!inferredUserId && !deviceToken) {
      console.log('[Socket] Waiting for userId or deviceToken...');
      return;
    }

    console.log('[Socket] Registering device...');
    emitSocketEvent(socketRef, SOCKET_EVENTS.REGISTER_DEVICE, {
      deviceToken: deviceToken || null,
      userId: inferredUserId || null,
      location,
    });
    registeredRef.current = true;
    console.log('[Socket] Sent REGISTER_DEVICE', {
      deviceToken: !!deviceToken,
      userId: inferredUserId,
    });
  }, [socketRef.current?.connected, deviceToken, inferredUserId, location]);

  return socketRef.current;
}
